import axios from "axios";
import {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import common from "@/config/common";
import router from "@/router";
import * as apiClient from "@/apiclient/";
import Loader from "@/components/common/LoaderComponent.vue";
import { createApp, h } from "vue";
import store from "@/store";
import messages from "@/config/messages";
import TokenService from "@/services/tokenService";

declare const AXIOS_HEADERS: string;

export type RequestConfig<TVariables = unknown> = {
  method:
    | "get"
    | "put"
    | "patch"
    | "post"
    | "delete"
    | "GET"
    | "PUT"
    | "PATCH"
    | "POST"
    | "DELETE";
  url: string;
  params?: unknown;
  data?: TVariables | FormData;
  responseType?:
    | "arraybuffer"
    | "blob"
    | "document"
    | "json"
    | "text"
    | "stream";
  signal?: AbortSignal;
  headers?: AxiosRequestConfig["headers"];
};

export type ResponseErrorConfig<TData = unknown> = {
  data?: TData;
  status: number;
  statusText: string;
  response?: TData;
  error?: string;
  headers?: AxiosResponse["headers"];
};

export type ResponseConfig<TData = unknown> = {
  data?: TData;
  status: number;
  statusText: string;
  response?: TData;
  error?: string;
  headers?: AxiosResponse["headers"];
};

export const axiosInstance = axios.create({
  baseURL: store.state.settings.apiBaseUrl,
  headers:
    typeof AXIOS_HEADERS !== "undefined"
      ? (JSON.parse(AXIOS_HEADERS) as AxiosHeaders)
      : undefined,
});

export const axiosClient = async <
  TData,
  TError = unknown,
  TVariables = unknown
>(
  config: RequestConfig<TVariables>
): Promise<ResponseConfig<TData>> => {
  showLoader();

  try {
    config = await SetAuthHeader(config);
    axiosInstance.defaults.baseURL = store.state.settings.apiBaseUrl;
    const response = await axiosInstance.request<TData, ResponseConfig<TData>>(
      config
    );
    return response;
  } catch (e: unknown) {
    if (axios.isAxiosError(e)) {
      if (e.response && e.response.status === 401) {
        try {
          return await RefreshKeyAndRetryRequest<TData, ResponseConfig<TData>>(
            config,
            e
          );
        } catch (retryError) {
          console.error("Token refresh failed:", retryError);
          oopsModal(e);
          return Promise.reject(messages.oopsError);
        }
      } else if (e.response && e.response.status === 400) {
        const transformedResponse = {
          status: e.response.status,
          statusText: e.response.statusText,
          error: e.response.data.ErrorMessage,
        };
        return transformedResponse as ResponseConfig<TData>;
      } else {
        console.error("API error:", e);
        oopsModal(e);
        return Promise.reject(messages.oopsError);
      }
    } else {
      console.error("Unexpected error:", e);
      oopsModal(e);
      return Promise.reject(messages.oopsError);
    }
  } finally {
    hideLoader();
  }
};

async function SetAuthHeader<TVariables = unknown>(
  config: RequestConfig<TVariables>
): Promise<RequestConfig<TVariables>> {
  var accessToken = sessionStorage.getItem(common.storageVariables.accessToken);

  if (accessToken) {
    config.headers = {
      Authorization: "Bearer " + accessToken,
    };
  }

  return config;
}

async function RefreshKeyAndRetryRequest<
  TData,
  TError = unknown,
  TVariables = unknown
>(
  config: RequestConfig<TVariables>,
  error: AxiosError<any>
): Promise<ResponseConfig<TData>> {
  const key = await RefreshKey(error);
  if (!key) {
    throw "Communication Error";
  }
  config.headers = {
    Authorization: "Bearer " + key,
  };
  const promise = axiosInstance
    .request<TData, ResponseConfig<TData>>(config)
    .catch((e: AxiosError<TError>) => {
      throw e;
    });

  return promise;
}
async function RefreshKey(error: AxiosError<any>): Promise<string | null> {
  var newAccessToken = null;
  try {
    const params = {
      refreshToken: TokenService.getRefreshToken(),
    };
    const result = await axios({
      method: "POST",
      url: store.state.settings.apiBaseUrl + "api/Login/refresh-token",
      withCredentials: false,
      data: params,
    }).then((result) => {
      if (result) {
        newAccessToken = result.data?.token!;
        TokenService.setAccessToken(newAccessToken);
        TokenService.setRefreshToken(result.data?.refreshToken!);
      } else {
        router.push("/");
        throw error;
      }
      return Promise.resolve(result);
    });
  } catch (refreshError) {
    router.push("/");
    throw refreshError;
  }
  return newAccessToken;
}

function oopsModal(error: any) {
  if (error.response?.status === 401) {
    return;
  }
  store.commit("setShowModal", true);
  store.commit("setModalTitle", messages.oopsError);
  store.commit("setModalContent", messages.oopsErrorContent);
}

let loaderCounter = 0;
function showLoader() {
  loaderCounter++;
  const loaderElement = document.querySelector(".loader");
  if (loaderElement || loaderCounter > 1) {
    return;
  }
  const loaderApp = createApp({
    render: () => h(Loader),
  });

  const loaderComponent = loaderApp.mount(document.createElement("div")); // Mount the loader component to a temporary div
  document.body.appendChild(loaderComponent.$el);
}

function hideLoader() {
  setTimeout(() => {
    loaderCounter--;
    if (!loaderCounter) {
      const loaderElement = document.querySelector(".loader");
      if (loaderElement) {
        loaderElement.remove(); // Remove the loader component from the DOM
      }
    }
  }, 2000);
}

export default axiosClient;
